import { refreshEntitlement } from '../hooks/usePlan';

/**
 * Paddle.js v2 loader — injects the CDN script tag and calls
 * Paddle.Environment.set()/Paddle.Initialize() exactly once per session,
 * however many times PricingPage mounts/unmounts. No npm dependency: Paddle
 * ships as a runtime <script> per their own integration docs.
 *
 * environment is whatever /billing/config returns ('sandbox' while payments
 * are dark/testing, 'production' once live) — passed straight through to
 * Paddle.Environment.set(), which is the dark-vs-live switch.
 *
 * The single Initialize() call registers an eventCallback that refetches
 * the entitlement (busting usePlan's cache) whenever a checkout closes or
 * completes, so the plan badge/gates update without a page reload —
 * regardless of which PricingPage instance opened the checkout.
 */
let loadPromise = null;
let initialized = false;

function injectScript() {
  return new Promise((resolve, reject) => {
    if (window.Paddle) {
      resolve(window.Paddle);
      return;
    }
    const existing = document.querySelector('script[data-lnc-paddle]');
    if (existing) {
      existing.addEventListener('load', () => resolve(window.Paddle));
      existing.addEventListener('error', () => reject(new Error('Failed to load Paddle.js')));
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://cdn.paddle.com/paddle/v2/paddle.js';
    script.async = true;
    script.dataset.lncPaddle = 'true';
    script.onload = () => resolve(window.Paddle);
    script.onerror = () => reject(new Error('Failed to load Paddle.js'));
    document.head.appendChild(script);
  });
}

export function loadPaddle(environment, clientToken) {
  if (initialized && window.Paddle) {
    return Promise.resolve(window.Paddle);
  }
  if (loadPromise) {
    return loadPromise;
  }

  loadPromise = injectScript()
    .then((Paddle) => {
      if (!initialized) {
        Paddle.Environment.set(environment);
        Paddle.Initialize({
          token: clientToken,
          eventCallback(event) {
            const name = event?.name;
            if (name === 'checkout.closed' || name === 'checkout.completed') {
              refreshEntitlement();
            }
          }
        });
        initialized = true;
      }
      return Paddle;
    })
    .catch((error) => {
      loadPromise = null;
      throw error;
    });

  return loadPromise;
}

export default loadPaddle;
